import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { storage } from "@/modules/storage";
import {
  getAdonisTopRecommendationsId,
  getUser,
  getUserPreferences,
  postListingsBasicRecommended,
} from "api/methods";
import { PreparingScreen } from "modules/personalisation/components/PreparingScreen";

import { PersonalisationWelcomeContainer_PATH } from "./PersonalisationWelcomeContainer";

export const PreparingScreenContainer_PATH = "/personalise/preparing" as const;

export function PreparingScreenContainer() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isFromOnboarding] = useState(
    () => storage.getItem("personalisation_from_onboarding") === "true",
  );

  const { data: user } = useQuery({
    ...getUser.keyed(),
    staleTime: 1000 * 60 * 2,
  });

  const regenerateRecommendations = useMutation({
    mutationFn: () => postListingsBasicRecommended(undefined),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: getAdonisTopRecommendationsId.key({}),
      });
    },
  });

  // Safety-net: regenerate recommendations after a short delay.
  // The preference save from the concerns step uses FireAndForgetAsync,
  // so by the time this screen loads (several steps later) the concerns
  // should be committed. A small extra delay adds further safety margin.
  useEffect(() => {
    const timer = setTimeout(() => {
      regenerateRecommendations.mutate();
    }, 1500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const destination = isFromOnboarding
      ? PersonalisationWelcomeContainer_PATH
      : "/home?fromPersonalisation=true";

    const id = setTimeout(() => {
      storage.removeItem("personalisation_from_onboarding");
      queryClient.invalidateQueries({ queryKey: getUserPreferences.key() });
      queryClient.invalidateQueries({
        queryKey: getAdonisTopRecommendationsId.key({}),
      });
      navigate(destination);
    }, 7000);
    return () => clearTimeout(id);
  }, [navigate, isFromOnboarding, queryClient]);

  return <PreparingScreen durationMs={1200} userFirstName={user?.firstName} />;
}
